import { useState, useMemo, useId, useRef, useEffect } from 'react';
import {
  Users,
  Search,
  Mail,
  Phone,
  Plus,
  X,
  Star,
  Loader2,
  Globe,
  Calendar,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Send,
  ShieldCheck,
  Save,
} from 'lucide-react';
import { BookingSource, BookingStatus } from '@pms/shared';
import { cn } from '@/lib/utils';
import {
  useGuests,
  useGuest,
  useGuestBookings,
  useCreateGuest,
  useUpdateGuest,
  useAddGuestNote,
  useGuestCommunications,
} from '@/hooks/api/useGuests';
import { useSelectedPropertyId, usePropertyContext } from '@/contexts/PropertyContext';
import type { EmailLog } from '@/services/api/notifications.api';

// ---- Constants ----

const SOURCE_LABELS: Record<string, string> = {
  [BookingSource.AIRBNB]: 'Airbnb',
  [BookingSource.BOOKING_COM]: 'Booking.com',
  [BookingSource.EXPEDIA]: 'Expedia',
  [BookingSource.DIRECT]: 'Direct',
  [BookingSource.MANUAL]: 'Manual',
};

const STATUS_STYLES: Record<string, string> = {
  [BookingStatus.CONFIRMED]: 'bg-green-100 text-green-800',
  [BookingStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
  [BookingStatus.CHECKED_IN]: 'bg-blue-100 text-blue-800',
  [BookingStatus.CHECKED_OUT]: 'bg-gray-100 text-gray-600',
  [BookingStatus.CANCELLED]: 'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<string, string> = {
  [BookingStatus.CONFIRMED]: 'Confirmed',
  [BookingStatus.PENDING]: 'Pending',
  [BookingStatus.CHECKED_IN]: 'Checked In',
  [BookingStatus.CHECKED_OUT]: 'Checked Out',
  [BookingStatus.CANCELLED]: 'Cancelled',
};

// ---- Helpers ----

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

// ---- Email Status ----

const EMAIL_STATUS_STYLES: Record<string, string> = {
  queued: 'bg-gray-100 text-gray-600',
  sent: 'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  bounced: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-700',
  opened: 'bg-emerald-100 text-emerald-700',
};

const EMAIL_STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  sent: 'Sent',
  delivered: 'Delivered',
  bounced: 'Bounced',
  failed: 'Failed',
  opened: 'Opened',
};

// ---- Communication Entry ----

function CommunicationEntry({ log }: { log: EmailLog }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <li className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="w-full flex items-start gap-3 px-3 py-3 text-left hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-50 text-primary-600 shrink-0 mt-0.5">
          <Send size={14} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 truncate">
              {log.subject}
            </span>
            <span
              className={cn(
                'inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium shrink-0',
                EMAIL_STATUS_STYLES[log.status] ?? 'bg-gray-100 text-gray-600',
              )}
            >
              {EMAIL_STATUS_LABELS[log.status] ?? log.status}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            <span>{log.sentAt ? formatDate(log.sentAt) : formatDate(log.createdAt)}</span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 text-xs">
              {log.templateType}
            </span>
            <span className="truncate">{log.recipientEmail}</span>
          </div>
        </div>
        <span className="text-gray-400 shrink-0 mt-1" aria-hidden="true">
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>
      {isExpanded && (
        <div className="px-3 pb-3 border-t border-gray-100">
          <div className="mt-2 space-y-1.5 text-xs text-gray-500">
            <p><span className="font-medium text-gray-600">To:</span> {log.recipientEmail}</p>
            {log.sentAt && <p><span className="font-medium text-gray-600">Sent:</span> {formatDate(log.sentAt)}</p>}
            {log.deliveredAt && <p><span className="font-medium text-gray-600">Delivered:</span> {formatDate(log.deliveredAt)}</p>}
            {log.openedAt && <p><span className="font-medium text-gray-600">Opened:</span> {formatDate(log.openedAt)}</p>}
            {log.errorMessage && (
              <p className="text-red-600"><span className="font-medium">Error:</span> {log.errorMessage}</p>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

// ---- Guest Communications Section ----

function GuestCommunicationsSection({ guestId }: { guestId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useGuestCommunications(guestId, { page });

  const logs: EmailLog[] = data?.data ?? [];
  const meta = data?.meta;
  const totalPages = meta ? Math.ceil(meta.total / meta.limit) : 1;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Loader2 size={16} className="animate-spin text-primary-600" aria-hidden="true" />
        <span className="text-sm text-gray-500">Loading communications...</span>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-center">
        <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
          <Mail size={20} className="text-gray-400" aria-hidden="true" />
        </div>
        <p className="text-sm text-gray-500">No communications sent to this guest yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <ul className="space-y-2" aria-label="Guest communications">
        {logs.map((log) => (
          <CommunicationEntry key={log.id} log={log} />
        ))}
      </ul>

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className={cn(
              'px-3 py-1 rounded-lg text-xs font-medium border transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              page <= 1
                ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50',
            )}
          >
            Previous
          </button>
          <span className="text-xs text-gray-500">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className={cn(
              'px-3 py-1 rounded-lg text-xs font-medium border transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              page >= totalPages
                ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50',
            )}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ---- New Guest Modal ----

interface NewGuestFormData {
  name: string;
  email: string;
  phone: string;
  nationality: string;
}

const EMPTY_GUEST_FORM: NewGuestFormData = {
  name: '',
  email: '',
  phone: '',
  nationality: '',
};

function NewGuestModal({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: NewGuestFormData) => void;
}) {
  const [form, setForm] = useState<NewGuestFormData>(EMPTY_GUEST_FORM);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const dialogRef = useRef<HTMLDialogElement>(null);
  const headingId = useId();
  const nameId = useId();
  const emailId = useId();
  const phoneId = useId();
  const nationalityId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen && !dialog.open) {
      dialog.showModal();
      setErrors({});
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const newErrors: { name?: string; email?: string } = {};
    if (!form.name.trim()) newErrors.name = 'Full name is required.';
    if (!form.email.trim()) newErrors.email = 'Email address is required.';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    onSubmit(form);
    setForm(EMPTY_GUEST_FORM);
  }

  function update<K extends keyof NewGuestFormData>(
    key: K,
    value: NewGuestFormData[K],
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  if (!isOpen) return null;

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="fixed inset-0 z-50 m-auto w-full max-w-md rounded-xl bg-white shadow-xl border border-gray-200 p-0 backdrop:bg-black/40"
      aria-labelledby={headingId}
    >
      <form onSubmit={handleSubmit} className="flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 id={headingId} className="text-lg font-semibold text-gray-900">
            New Guest
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            aria-label="Close dialog"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label htmlFor={nameId} className="block text-xs font-medium text-gray-600 mb-1">
              Full Name <span aria-label="required">*</span>
            </label>
            <input id={nameId} type="text" required value={form.name}
              onChange={(e) => { update('name', e.target.value); if (errors.name) setErrors((p) => { const { name: _, ...rest } = p; return rest; }); }}
              placeholder="John Doe"
              className={cn('w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-800', 'placeholder:text-gray-400', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500', errors.name ? 'border-red-400' : 'border-gray-300')} />
            {errors.name && <p role="alert" className="mt-1 text-xs text-red-600">{errors.name}</p>}
          </div>
          <div>
            <label htmlFor={emailId} className="block text-xs font-medium text-gray-600 mb-1">
              Email <span aria-label="required">*</span>
            </label>
            <input id={emailId} type="email" required value={form.email}
              onChange={(e) => { update('email', e.target.value); if (errors.email) setErrors((p) => { const { email: _, ...rest } = p; return rest; }); }}
              placeholder="john@example.com"
              className={cn('w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-800', 'placeholder:text-gray-400', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500', errors.email ? 'border-red-400' : 'border-gray-300')} />
            {errors.email && <p role="alert" className="mt-1 text-xs text-red-600">{errors.email}</p>}
          </div>
          <div>
            <label htmlFor={phoneId} className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
            <input id={phoneId} type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+1 (555) 000-0000" className={cn('w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800', 'placeholder:text-gray-400', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500')} />
          </div>
          <div>
            <label htmlFor={nationalityId} className="block text-xs font-medium text-gray-600 mb-1">Nationality</label>
            <input id={nationalityId} type="text" value={form.nationality} onChange={(e) => update('nationality', e.target.value)} placeholder="e.g. US, DE, FR" className={cn('w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800', 'placeholder:text-gray-400', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500')} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">Cancel</button>
          <button type="submit" className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2">Add Guest</button>
        </div>
      </form>
    </dialog>
  );
}

// ---- Guest Slide-over Panel ----

function GuestSlideOver({
  guestId,
  onClose,
}: {
  guestId: string;
  onClose: () => void;
}) {
  const [noteInput, setNoteInput] = useState('');
  const [editingId, setEditingId] = useState(false);
  const [idForm, setIdForm] = useState({
    idType: '',
    idNumber: '',
    idIssuingCountry: '',
    idExpiryDate: '',
  });
  const panelRef = useRef<HTMLDivElement>(null);
  const headingId = useId();
  const noteInputId = useId();
  const idTypeFieldId = useId();
  const idNumberFieldId = useId();
  const idCountryFieldId = useId();
  const idExpiryFieldId = useId();

  const { data: guestData, isLoading: guestLoading } = useGuest(guestId);
  const { data: guestBookingsData, isLoading: bookingsLoading } = useGuestBookings(guestId);
  const addNoteMutation = useAddGuestNote();
  const updateGuestMutation = useUpdateGuest();

  const guest: Record<string, unknown> = useMemo(() => {
    if (!guestData) return {};
    return guestData;
  }, [guestData]);

  const bookings: Array<Record<string, unknown>> = useMemo(() => {
    if (!guestBookingsData) return [];
    return Array.isArray(guestBookingsData) ? guestBookingsData : (guestBookingsData.data ?? []);
  }, [guestBookingsData]);

  const totalSpend = useMemo(() =>
    bookings.reduce((sum, bk) => {
      const amt = typeof bk.totalAmount === 'number'
        ? bk.totalAmount
        : parseFloat(String(bk.totalAmount ?? '0')) || 0;
      return sum + amt;
    }, 0),
    [bookings],
  );

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  function handleAddNote(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = noteInput.trim();
    if (!trimmed) return;
    addNoteMutation.mutate({ id: guestId, note: trimmed });
    setNoteInput('');
  }

  const name = (guest.fullName as string) ?? (guest.name as string) ?? '';
  const email = (guest.email as string) ?? '';
  const phone = (guest.phone as string) ?? '';
  const nationality = (guest.nationality as string) ?? '';
  const isRepeat = (guest.isRepeatGuest as boolean) ?? (guest.isRepeat as boolean) ?? false;
  const bookingCount = (guest.bookingCount as number) ?? bookings.length;
  const lastStay = (guest.lastStay as string) ?? '';
  const notes: string[] = Array.isArray(guest.notes) ? (guest.notes as string[]) : [];

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" aria-hidden="true" onClick={onClose} />
      <aside
        ref={panelRef}
        className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-xl border-l border-gray-200 flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <h2 id={headingId} className="text-lg font-semibold text-gray-900">Guest Profile</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500" aria-label="Close guest details">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {guestLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={20} className="animate-spin text-primary-600" />
              <span className="ml-2 text-sm text-gray-500">Loading guest...</span>
            </div>
          ) : (
            <div className="px-5 py-4 space-y-6">
              {/* Guest identity */}
              <section aria-label="Guest contact information">
                <div className="flex items-start gap-4">
                  <div
                    className="flex items-center justify-center w-14 h-14 rounded-full bg-primary-100 text-primary-700 text-xl font-bold shrink-0"
                    aria-hidden="true"
                  >
                    {name ? getInitials(name) : '?'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-bold text-gray-900">{name || 'Unknown Guest'}</h3>
                      {isRepeat && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                          <Star size={10} aria-hidden="true" />
                          VIP Repeat
                        </span>
                      )}
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {email && (
                        <a
                          href={`mailto:${email}`}
                          className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                        >
                          <Mail size={13} aria-hidden="true" />
                          {email}
                        </a>
                      )}
                      {phone && (
                        <a
                          href={`tel:${phone}`}
                          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                        >
                          <Phone size={13} aria-hidden="true" />
                          {phone}
                        </a>
                      )}
                      {nationality && (
                        <p className="flex items-center gap-2 text-sm text-gray-600">
                          <Globe size={13} aria-hidden="true" />
                          {nationality}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <div className="bg-gray-50 rounded-lg px-3 py-2.5 text-center">
                    <p className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-1">
                      <Calendar size={11} aria-hidden="true" />
                      Stays
                    </p>
                    <p className="text-lg font-bold text-gray-900">{bookingCount}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg px-3 py-2.5 text-center">
                    <p className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-1">
                      <DollarSign size={11} aria-hidden="true" />
                      Total Spend
                    </p>
                    <p className="text-lg font-bold text-gray-900">
                      ${totalSpend.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg px-3 py-2.5 text-center">
                    <p className="text-xs text-gray-500 mb-1">Last Stay</p>
                    <p className="text-sm font-semibold text-gray-700">
                      {lastStay ? formatDate(lastStay) : '—'}
                    </p>
                  </div>
                </div>
              </section>

              {/* ID Document */}
              <section aria-label="Identity document">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                    <ShieldCheck size={12} aria-hidden="true" />
                    ID Document
                  </h3>
                  {!editingId && (
                    <button
                      type="button"
                      onClick={() => {
                        setIdForm({
                          idType: (guest.idType as string) ?? '',
                          idNumber: (guest.idNumber as string) ?? '',
                          idIssuingCountry: (guest.idIssuingCountry as string) ?? '',
                          idExpiryDate: (guest.idExpiryDate as string) ?? '',
                        });
                        setEditingId(true);
                      }}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded px-1"
                    >
                      {(guest.idType as string) ? 'Edit' : 'Add ID'}
                    </button>
                  )}
                </div>

                {editingId ? (
                  <div className="space-y-3 bg-gray-50 rounded-lg p-3">
                    <div>
                      <label htmlFor={idTypeFieldId} className="block text-xs font-medium text-gray-600 mb-1">
                        ID Type <span className="text-gray-400 font-normal">(required)</span>
                      </label>
                      <select
                        id={idTypeFieldId}
                        value={idForm.idType}
                        onChange={(e) => setIdForm((prev) => ({ ...prev, idType: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select type</option>
                        <option value="passport">Passport</option>
                        <option value="national_id">National ID</option>
                        <option value="driving_license">Driving License</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor={idNumberFieldId} className="block text-xs font-medium text-gray-600 mb-1">ID Number</label>
                      <input
                        id={idNumberFieldId}
                        type="text"
                        value={idForm.idNumber}
                        onChange={(e) => setIdForm((prev) => ({ ...prev, idNumber: e.target.value }))}
                        placeholder="e.g. AB1234567"
                        className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor={idCountryFieldId} className="block text-xs font-medium text-gray-600 mb-1">Issuing Country</label>
                        <input
                          id={idCountryFieldId}
                          type="text"
                          value={idForm.idIssuingCountry}
                          onChange={(e) => setIdForm((prev) => ({ ...prev, idIssuingCountry: e.target.value }))}
                          placeholder="e.g. GB"
                          className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label htmlFor={idExpiryFieldId} className="block text-xs font-medium text-gray-600 mb-1">Expiry Date</label>
                        <input
                          id={idExpiryFieldId}
                          type="date"
                          value={idForm.idExpiryDate}
                          onChange={(e) => setIdForm((prev) => ({ ...prev, idExpiryDate: e.target.value }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setEditingId(false)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={!idForm.idType || updateGuestMutation.isPending}
                        onClick={() => {
                          const payload: Record<string, string> = {};
                          if (idForm.idType) payload.idType = idForm.idType;
                          if (idForm.idNumber) payload.idNumber = idForm.idNumber;
                          if (idForm.idIssuingCountry) payload.idIssuingCountry = idForm.idIssuingCountry;
                          if (idForm.idExpiryDate) payload.idExpiryDate = idForm.idExpiryDate;
                          updateGuestMutation.mutate(
                            {
                              id: guestId,
                              payload,
                            },
                            {
                              onSuccess: () => setEditingId(false),
                            },
                          );
                        }}
                        className={cn(
                          'inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                          idForm.idType && !updateGuestMutation.isPending
                            ? 'bg-primary-600 hover:bg-primary-700'
                            : 'bg-gray-300 cursor-not-allowed',
                        )}
                      >
                        <Save size={12} aria-hidden="true" />
                        Save
                      </button>
                      {updateGuestMutation.isError && (
                        <span className="text-xs text-red-600" role="alert">Failed to save</span>
                      )}
                    </div>
                  </div>
                ) : (guest.idType as string) ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <ShieldCheck size={14} className="text-green-600" aria-hidden="true" />
                      <span className="text-sm font-medium text-green-700 capitalize">
                        {((guest.idType as string) ?? '').replace(/_/g, ' ')}
                      </span>
                    </div>
                    <dl className="grid grid-cols-2 gap-y-1 text-xs">
                      {(guest.idNumber as string) && (
                        <>
                          <dt className="text-gray-500">Number</dt>
                          <dd className="text-gray-700 font-medium">{guest.idNumber as string}</dd>
                        </>
                      )}
                      {(guest.idIssuingCountry as string) && (
                        <>
                          <dt className="text-gray-500">Country</dt>
                          <dd className="text-gray-700">{guest.idIssuingCountry as string}</dd>
                        </>
                      )}
                      {(guest.idExpiryDate as string) && (
                        <>
                          <dt className="text-gray-500">Expires</dt>
                          <dd className="text-gray-700">{formatDate(guest.idExpiryDate as string)}</dd>
                        </>
                      )}
                    </dl>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No ID on file.</p>
                )}
              </section>

              {/* Booking History */}
              <section aria-label="Booking history">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Booking History
                </h3>
                {bookingsLoading ? (
                  <div className="flex items-center gap-2 py-4">
                    <Loader2 size={16} className="animate-spin text-primary-600" />
                    <span className="text-sm text-gray-500">Loading bookings...</span>
                  </div>
                ) : bookings.length === 0 ? (
                  <p className="text-sm text-gray-400">No booking history.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-100" aria-label="Guest booking history">
                      <thead>
                        <tr className="bg-gray-50">
                          {['Property / Unit', 'Dates', 'Source', 'Status', 'Amount'].map((col) => (
                            <th key={col} scope="col" className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {bookings.map((bk) => {
                          const propName = (bk.propertyName as string) ?? (bk.property as Record<string, unknown>)?.name ?? '';
                          const unitName = (bk.unitName as string) ?? (bk.unit as Record<string, unknown>)?.name ?? '';
                          const amt = typeof bk.totalAmount === 'number'
                            ? `$${(bk.totalAmount as number).toLocaleString()}`
                            : (bk.amount as string) ?? '—';
                          return (
                            <tr key={bk.id as string} className="hover:bg-gray-50 transition-colors">
                              <td className="px-3 py-2 whitespace-nowrap">
                                <p className="text-xs font-medium text-gray-800">{propName}</p>
                                {unitName && <p className="text-xs text-gray-500">{unitName}</p>}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                                {bk.checkIn ? formatDate(bk.checkIn as string) : ''} –{' '}
                                {bk.checkOut ? formatDate(bk.checkOut as string) : ''}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                                {SOURCE_LABELS[bk.source as string] ?? (bk.source as string)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium', STATUS_STYLES[bk.status as string] ?? 'bg-gray-100 text-gray-600')}>
                                  {STATUS_LABELS[bk.status as string] ?? (bk.status as string)}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-xs font-semibold text-gray-900 whitespace-nowrap">{amt}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Communications */}
              <section aria-label="Guest communications">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Communications
                </h3>
                <GuestCommunicationsSection guestId={guestId} />
              </section>

              {/* Notes from API */}
              {notes.length > 0 && (
                <section aria-label="Guest notes">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Notes</h3>
                  <ul className="space-y-2">
                    {notes.map((note, i) => (
                      <li key={i} className="text-sm text-gray-700 bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-2">
                        {note}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Add Note */}
              <section aria-label="Add guest note">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Add Note</h3>
                <form onSubmit={handleAddNote} className="flex gap-2">
                  <label htmlFor={noteInputId} className="sr-only">Add a note</label>
                  <input
                    id={noteInputId}
                    type="text"
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    placeholder="Add a note..."
                    className={cn('flex-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800', 'placeholder:text-gray-400', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500')}
                  />
                  <button
                    type="submit"
                    disabled={!noteInput.trim() || addNoteMutation.isPending}
                    className={cn('px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2', noteInput.trim() && !addNoteMutation.isPending ? 'bg-primary-600 hover:bg-primary-700' : 'bg-gray-300 cursor-not-allowed')}
                  >
                    Add
                  </button>
                </form>
                {addNoteMutation.isSuccess && (
                  <p className="mt-2 text-xs text-green-700" role="status" aria-live="polite">
                    Note added.
                  </p>
                )}
              </section>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

// ---- Page ----

export function GuestsPage() {
  const [search, setSearch] = useState('');
  const [repeatOnly, setRepeatOnly] = useState(false);
  const [isNewGuestOpen, setIsNewGuestOpen] = useState(false);
  const [selectedGuestId, setSelectedGuestId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const repeatToggleId = useId();
  const propertyId = useSelectedPropertyId();
  const { selectedProperty } = usePropertyContext();

  const { data: guestsData, isLoading, error } = useGuests({
    search: search || undefined,
    isRepeatGuest: repeatOnly || undefined,
    propertyId: propertyId || undefined,
    page,
    limit: PAGE_SIZE,
  });
  const createGuestMutation = useCreateGuest();

  const guests: Array<Record<string, unknown>> = useMemo(() => {
    if (!guestsData) return [];
    return Array.isArray(guestsData) ? guestsData : (guestsData.data ?? []);
  }, [guestsData]);

  const totalPages = (guestsData && !Array.isArray(guestsData) ? guestsData.meta?.totalPages : null) ?? 1;
  const totalGuests = (guestsData && !Array.isArray(guestsData) ? guestsData.meta?.total : null) ?? guests.length;
  const scopeLabel = selectedProperty ? selectedProperty.name : 'this property';

  function handleNewGuest(data: NewGuestFormData) {
    createGuestMutation.mutate({
      fullName: data.name,
      email: data.email,
      phone: data.phone || undefined,
      nationality: data.nationality || undefined,
    });
    setIsNewGuestOpen(false);
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Guests</h1>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm text-red-700">Failed to load guests. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Guests</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {totalGuests} {totalGuests === 1 ? 'guest' : 'guests'} in {scopeLabel}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setIsNewGuestOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <Plus size={16} aria-hidden="true" />
          New Guest
        </button>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name or email..."
            aria-label="Search guests"
            className={cn('w-full pl-9 pr-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-800 bg-white', 'placeholder:text-gray-400', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500')}
          />
        </div>

        <label htmlFor={repeatToggleId} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input id={repeatToggleId} type="checkbox" checked={repeatOnly} onChange={(e) => { setRepeatOnly(e.target.checked); setPage(1); }} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          Repeat guests only
        </label>
      </div>

      <p className="sr-only" aria-live="polite">
        {guests.length} {guests.length === 1 ? 'guest' : 'guests'} found
      </p>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-primary-600" aria-hidden="true" />
            <span className="ml-2 text-sm text-gray-500">Loading guests...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100" aria-label="Guest list">
              <thead>
                <tr className="bg-gray-50">
                  {['Guest', 'Email', 'Phone', 'Nationality', 'Total Bookings', 'Last Stay'].map((col) => (
                    <th key={col} scope="col" className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {guests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-16">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
                          <Users size={24} className="text-gray-400" aria-hidden="true" />
                        </div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">No guests yet</h3>
                        <p className="text-sm text-gray-500 max-w-xs">
                          {search || repeatOnly ? 'No guests match your search criteria.' : 'Guest profiles will appear here after the first booking.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  guests.map((guest) => {
                    const name = (guest.fullName as string) ?? (guest.name as string) ?? '';
                    const isRepeat = (guest.isRepeatGuest as boolean) ?? (guest.isRepeat as boolean) ?? false;
                    const totalBookings = (guest.bookingCount as number) ?? (guest.totalBookings as number) ?? 0;
                    const lastStay = (guest.lastStay as string) ?? '';
                    return (
                      <tr
                        key={guest.id as string}
                        onClick={() => setSelectedGuestId(guest.id as string)}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        tabIndex={0}
                        role="button"
                        aria-label={`View details for ${name}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedGuestId(guest.id as string);
                          }
                        }}
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 text-xs font-bold shrink-0" aria-hidden="true">
                              {getInitials(name)}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900">{name}</span>
                              {isRepeat && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                                  <Star size={9} aria-hidden="true" />
                                  Repeat
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="flex items-center gap-1.5 text-sm text-primary-600">
                            <Mail size={13} aria-hidden="true" />
                            {guest.email as string}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="flex items-center gap-1.5 text-sm text-gray-600">
                            <Phone size={13} aria-hidden="true" />
                            {(guest.phone as string) ?? '-'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-600">
                          {(guest.nationality as string) || '-'}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-600">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold">
                            {totalBookings} {totalBookings === 1 ? 'booking' : 'bookings'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-600">
                          {lastStay ? formatDate(lastStay) : '-'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* New Guest Modal */}
      <NewGuestModal
        isOpen={isNewGuestOpen}
        onClose={() => setIsNewGuestOpen(false)}
        onSubmit={handleNewGuest}
      />

      {/* Guest Slide-over */}
      {selectedGuestId && (
        <GuestSlideOver
          guestId={selectedGuestId}
          onClose={() => setSelectedGuestId(null)}
        />
      )}
    </div>
  );
}
